<template>
  <v-container fluid pt-0>
    <v-row align="center" class="pb-2">
      <h1 class="headline">Flights</h1>
      <v-spacer></v-spacer>
      <v-col class="py-0">
        <list-filters
          :filters="filters"
          label="Find flight"
          ref-name="flights_list_filters"
          @searchParamsChanged="onFiltersChange"
          :expansionPanel="false"
        ></list-filters>
      </v-col>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" fab dark color="primary" @click="onCreateNew">
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Create New Flight</span>
      </v-tooltip>
    </v-row>

    <v-row>
      <v-col class="pa-0">
        <v-data-table
          show-select
          v-model="selected"
          :headers="headers"
          :items="items"
          :loading="$apollo.loading"
          :search="search"
        >
          <template v-slot:item.supplierName="{ item }">
            <v-row align="center">
              {{ item.supplier ? item.supplier.name : "" }}
            </v-row>
          </template>
          <template v-slot:item.isException="{ item }">
            <v-checkbox
              v-model="item.isException"
              primary
              hide-details
              :disabled="true"
              class="ma-0"
            ></v-checkbox>
          </template>
          <template v-slot:item.validFrom="{ item }">{{
            item.validFrom | formatDate
          }}</template>
          <template v-slot:item.validTo="{ item }">{{
            item.validTo | formatDate
          }}</template>
          <template v-slot:item.weekDays="{ item }">
            {{ daysLabeler(item.weekDays) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              hide-view
              @deleteItem="onDeleteItem"
              @editItem="onEditItem"
            >
            </default-list-actions>
          </template>
          <template v-slot:footer>
            <list-actions
              v-if="selected.length > 0"
              :selected="selected"
              list="flights"
              :columns="headers.map((c) => c.name)"
              :filter="searchParams"
              @close="selected = []"
            >
            </list-actions>
            <v-row justify="center" v-if="selected.length > 0">
              <h3 class="py-2 caption">
                {{ selected.length }} record(s) selected
              </h3>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row v-if="debug">
      <code>{{}}</code>
    </v-row>
    <flight-edit-dialog
      :show="showFlightEditDialog"
      @close="showFlightEditDialog = false"
      v-model="editedFlight"
      @input="onSave"
      :forSupplier="forSupplier"
    ></flight-edit-dialog>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { ListMixin } from "../../../../mixins/ListMixin";
import { removeGQLTypename } from "../../../../helpers";
import DefaultListActions from "../../shared/DefaultListActions";
import FlightEditDialog from "./FlightEditDialog";
import ListFilters from "../../shared/list-filters/ListFilters";
import listFilters from "./list-filters";
import ListActions from "../../shared/list-actions/ListActions";
const LIST_QUERY = gql`
  query flights($filter: FlightFilterType) {
    flights(filter: $filter) {
      edges {
        node {
          id
          validFrom
          validTo
          flightNo
          flightType
          weekDays
          departureTime
          departurePoint
          arrivalTime
          arrivalPoint
          isException
          supplierId
          supplier {
            name
          }
        }
      }
    }
  }
`;
const UPDATE_MUTATION = gql`
  mutation updateFlight($id: ID!, $input: FlightInput!) {
    updateFlight(id: $id, flightInput: $input) {
      id
      validFrom
      validTo
      flightNo
      flightType
      weekDays
      supplierId
      departureTime
      departurePoint
      arrivalTime
      arrivalPoint
      isException
    }
  }
`;
const CREATE_MUTATION = gql`
  mutation createFlight($input: FlightInput!) {
    createFlight(flightInput: $input) {
      id
      validFrom
      validTo
      flightNo
      flightType
      weekDays
      supplierId
      departureTime
      departurePoint
      arrivalTime
      arrivalPoint
      isException
    }
  }
`;
const DELETE_MUTATION = gql`
  mutation deleteFlight($id: ID!) {
    deleteFlight(id: $id) {
      id
    }
  }
`;

export default {
  name: "flight-list",

  mixins: [ListMixin],

  components: {
    DefaultListActions,
    FlightEditDialog,
    ListFilters,
    ListActions,
  },
  props: {
    customFilters: {
      type: Object,
    },
  },
  data() {
    return {
      selected: [],
      debug: false,
      headers: [
        {
          text: "Valid From",
          value: "validFrom",
          name: "validFrom",
        },
        {
          text: "Valid To",
          value: "validTo",
          name: "validTo",
        },
        {
          text: "Flight Type",
          value: "flightType",
          name: "flightType",
        },
        {
          text: "Flight No",
          value: "flightNo",
          name: "flightNo",
        },
        {
          text: "Week Days",
          value: "weekDays",
          name: "weekDays",
        },
        {
          text: "Departure Point",
          value: "departurePoint",
          name: "departurePoint",
        },
        {
          text: "Departure Time",
          value: "departureTime",
          name: "departureTime",
        },
        {
          text: "Arrival Point",
          value: "arrivalPoint",
          name: "arrivalPoint",
        },
        {
          text: "Arrival Time",
          value: "arrivalTime",
          name: "arrivalTime",
        },
        {
          text: "Supplier",
          value: "supplierName",
          name: "supplierName",
        },
        {
          text: "Is Exception",
          value: "isException",
          name: "isException",
        },
        {
          text: "Actions",
          value: "actions",
          name: "actions",
        },
      ],
      editedFlight: null,
      showFlightEditDialog: false,
    };
  },

  computed: {
    forSupplier() {
      return !!(this.customFilters && this.customFilters.supplierId);
    },
    items() {
      console.log("items", this.flights);
      return this.flights && this.flights.edges
        ? this.flights.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return listFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
  },

  methods: {
    daysLabeler(daysOfWeek) {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return daysOfWeek
        .split(",")
        .map((d) => days[d])
        .join(", ");
    },
    onFiltersChange(filters) {
      this.first = 50;
      this.searchParams = { ...filters };
    },
    onCreateNew() {
      this.editedFlight = this.customFilters;
      this.showFlightEditDialog = true;
    },
    onEditItem(item) {
      this.editedFlight = { ...item };
      this.showFlightEditDialog = true;
    },
    async onSave(payload) {
      console.log("saving flight", payload);
      this.showFlightEditDialog = false;

      const mutation = payload.id ? UPDATE_MUTATION : CREATE_MUTATION;
      try {
        await this.$apollo.mutate({
          mutation: mutation,
          variables: { ...payload },
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Flight Saved.",
        });
        this.$apollo.queries.flights.refetch();
      } catch (error) {
        console.log(error);
        const errorMsg = error.graphQLErrors.map((e) => e.message).join(", ");
        this.$store.dispatch("showAppSnackbar", {
          msg: errorMsg,
          color: "error",
        });
      }
    },
    async onDeleteItem(payload) {
      try {
        await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: {
            id: payload.id,
          },
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Flight deleted",
        });
        this.$apollo.queries.flights.refetch();
      } catch (error) {
        console.log(error);
      }
    },
  },

  apollo: {
    flights: {
      query: LIST_QUERY,
      variables() {
        return {
          filter: { ...this.searchParams, ...this.customFilters },
        };
      },
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/plugins/flights/FlightsList.vue